import { Tracker, UserFactory } from '@relewise/client'

import { env } from '@/lib/env'
import { eventBus } from '@/lib/eventBus/eventBus'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type { User } from '@relewise/client'
import type { Cart, Order, OrderEntry } from '@/gen/ecom'
import type { TrackingContext } from '@/lib/eventBus/trackingEvents'

// AZ_ENV, not NODE_ENV: preview/staging builds are also NODE_ENV=production (same gate as GA4).
const isProduction = () => env.AZ_ENV === 'p'

const createTracker = () => {
  const datasetId = env.NEXT_PUBLIC_RELEWISE_DATASET_ID
  const apiKey = env.NEXT_PUBLIC_RELEWISE_API_KEY
  const serverUrl = env.NEXT_PUBLIC_RELEWISE_SERVER_URL

  return datasetId && apiKey && serverUrl ? new Tracker(datasetId, apiKey, { serverUrl }) : null
}

const logRequest = (label: string, request: unknown, isSent: boolean) => {
  console.groupCollapsed(`[Relewise] ${label}${isSent ? ' (sent)' : ' (not sent — not configured)'}`)
  console.warn('Request Data:', request)
  console.groupEnd()
}

const createSender =
  (tracker: Tracker | null) =>
  async <Request>(
    label: string,
    request: Request,
    deliver: (tracker: Tracker, request: Request) => Promise<unknown>
  ) => {
    if (!isProduction()) {
      logRequest(label, request, tracker !== null)
    }

    if (tracker) {
      await deliver(tracker, request)
    }
  }

/** The shared TemporaryId the BFF sends for every visitor without marketing consent. Never link it to an
 * AuthenticatedId on login: that would merge all logged-in users into one Relewise user. */
const BFF_ANONYMOUS_TEMPORARY_ID = 'anonymous'

/** Mirrors the User the BFF builds for Relewise searches, so searches, views, carts and orders join to one user. */
const toRelewiseUser = ({ authenticatedUserId, marketingConsentId, marketDomain }: TrackingContext): User => {
  const segmentation: Pick<User, 'classifications' | 'channel'> = {
    classifications: marketDomain ? { 'market-domain': marketDomain } : {},
    channel: { name: 'website', subChannel: marketDomain ? { name: marketDomain } : null },
  }

  if (authenticatedUserId) {
    return UserFactory.byAuthenticatedId(authenticatedUserId, marketingConsentId, segmentation)
  }

  return UserFactory.byTemporaryId(marketingConsentId ?? BFF_ANONYMOUS_TEMPORARY_ID, segmentation)
}

const toLineItems = (entries: OrderEntry[] = []) =>
  entries.flatMap(({ product, quantity, totalPrice }) =>
    product?.code ? [{ productId: product.code, quantity: quantity ?? 0, lineTotal: totalPrice?.value ?? 0 }] : []
  )

const toSubtotal = ({ subTotal }: Cart | Order, { currency }: TrackingContext) => ({
  amount: subTotal?.value ?? 0,
  currency: subTotal?.currencyIso ?? currency ?? '',
})

export const registerRelewiseSubscriber = () => {
  const tracker = createTracker()

  if (isProduction() && !tracker) {
    return () => undefined
  }

  const send = createSender(tracker)

  const subscriptions = [
    eventBus.subscribe(TrackingTopic.ItemViewed, async ({ context, data: { product } }) => {
      if (product.code) {
        await send(
          'trackProductView',
          { productId: product.code, user: toRelewiseUser(context) },
          (relewise, request) => relewise.trackProductView(request)
        )
      }
    }),
    eventBus.subscribe(TrackingTopic.ProductCategoryViewed, ({ context, data: { categoryIdPath } }) =>
      send('trackProductCategoryView', { idPath: categoryIdPath, user: toRelewiseUser(context) }, (relewise, request) =>
        relewise.trackProductCategoryView(request)
      )
    ),
    eventBus.subscribe(TrackingTopic.CartUpdated, ({ context, data: { cart } }) =>
      send(
        'trackCart',
        { user: toRelewiseUser(context), subtotal: toSubtotal(cart, context), lineItems: toLineItems(cart.entries) },
        (relewise, request) => relewise.trackCart(request)
      )
    ),
    eventBus.subscribe(TrackingTopic.OrderPlaced, async ({ context, data: { order } }) => {
      if (order.code) {
        await send(
          'trackOrder',
          {
            user: toRelewiseUser(context),
            orderNumber: order.code,
            subtotal: toSubtotal(order, context),
            lineItems: toLineItems(order.entries),
          },
          (relewise, request) => relewise.trackOrder(request)
        )
      }
    }),
    eventBus.subscribe(TrackingTopic.UserLoggedIn, ({ context }) =>
      send('trackUserUpdate', { user: toRelewiseUser(context) }, (relewise, request) =>
        relewise.trackUserUpdate(request)
      )
    ),
  ]

  return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
}
